import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { CardHeaderWithBadges } from '@/components/ui/CardHeader';
import { formatDate } from '@/utils/dateUtils';
import { MSAService } from '@/types';
import { Trash2 } from 'lucide-react';
import { CATEGORY_LABELS, ServiceCategory, getServiceIcon } from '@/constants/serviceCategories';
import { HealthStatus } from '@/types/health';

export interface ServiceCardProps {
  service: MSAService;
  healthStatus?: HealthStatus;
  isReadOnly?: boolean;
  onApprove?: (serviceId: number) => void;
  onReject?: (serviceId: number, serviceName: string) => void;
  onSuspend?: (serviceId: number, serviceName: string) => void;
  onResume?: (serviceId: number, serviceName: string) => void;
  onDelete?: (serviceId: number, serviceName: string) => void;
  onRestore?: (serviceId: number, serviceName: string) => void;
  onCancelRejection?: (serviceId: number, serviceName: string) => void;
  onToggleVisibility?: (serviceId: number, serviceName: string, newVisibility: boolean) => void;
  onServiceClick?: (service: MSAService) => void;
  onManagePlans?: (service: MSAService) => void;
  /** 로그인 연동(OIDC 클라이언트) 발급·관리 (#134) */
  onManageClient?: (service: MSAService) => void;
  getStatusBadge: (status: string | undefined) => React.ReactNode;
  getHealthBadge: (healthStatus: HealthStatus | undefined) => React.ReactNode;
}

const ServiceCard: React.FC<ServiceCardProps> = ({
  service,
  healthStatus,
  isReadOnly = false,
  onApprove,
  onReject,
  onSuspend,
  onResume,
  onDelete,
  onRestore,
  onCancelRejection,
  onToggleVisibility,
  onServiceClick,
  onManagePlans,
  onManageClient,
  getStatusBadge,
  getHealthBadge,
}) => {
  return (
    <Card className={`relative ${service.status === 'deleted' ? 'opacity-60' : ''}`}>
      <CardHeader>
        <div onClick={() => onServiceClick?.(service)} className="cursor-pointer hover:opacity-80">
          <CardHeaderWithBadges
            icon={getServiceIcon(service.metadata, service.category)}
            title={service.service_name}
            subtitle={CATEGORY_LABELS[service.category as ServiceCategory] || service.category}
            version={service.version}
            muted={service.status === 'deleted'}
            titleClassName="hover:text-blue-600"
            badges={[
              getStatusBadge(service.status),
              getHealthBadge(healthStatus),
            ].filter(Boolean) as React.ReactNode[]}
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {service.service_description && (
          <p className="text-sm text-gray-600 line-clamp-2">
            {service.service_description}
          </p>
        )}
        
        <div className="space-y-2 text-sm">
          <div>
            <span className="font-medium text-gray-700">서비스 URL:</span>
            <a
              href={service.service_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline ml-2 transition-colors duration-300"
            >
              {service.service_url}
            </a>
          </div>
          {service.contact_name && (
            <div>
              <span className="font-medium text-gray-700">개발사:</span>
              <span className="ml-2 text-gray-600">{service.contact_name}</span>
            </div>
          )}
          <div>
            <span className="font-medium text-gray-700">등록일:</span>
            <span className="ml-2 text-gray-600">
              {formatDate(service.created_at)}
            </span>
          </div>
          {/* 등록대기중, 삭제/거부된 서비스에서는 공개여부 표시하지 않음 (승인 시 결정) */}
          {service.is_public !== undefined && service.status === 'approved' && (
            <div className={isReadOnly ? '' : 'flex items-center justify-between'}>
              <span className="font-medium text-gray-700">공개 여부:</span>
              {isReadOnly ? (
                <span className={`ml-2 ${service.is_public ? 'text-green-600' : 'text-gray-600'}`}>
                  {service.is_public ? '공개' : '비공개'}
                </span>
              ) : (
                <Button
                  onClick={() => onToggleVisibility?.(service.id, service.service_name, !service.is_public)}
                  size="sm"
                  variant={service.is_public ? "default" : "outline"}
                  className={`ml-2 ${service.is_public ? 'bg-green-600 hover:bg-green-700' : ''}`}
                >
                  {service.is_public ? '🌐 공개' : '🔒 비공개'}
                </Button>
              )}
            </div>
          )}
        </div>

        {isReadOnly ? (
          <div className="pt-2 border-t text-center">
            <span className="text-xs text-gray-400">📋 상세 관리는 각 상태별 탭에서 가능합니다</span>
          </div>
        ) : service.status === 'deleted' ? (
          <div className="pt-2 border-t">
            <Button
              onClick={() => onRestore?.(service.id, service.service_name)}
              size="sm"
              variant="default"
              className="w-full"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              클리어
            </Button>
          </div>
        ) : service.status === 'rejected' ? (
          <div className="pt-2 border-t">
            <Button
              onClick={() => onCancelRejection?.(service.id, service.service_name)}
              size="sm"
              variant="default"
              className="w-full"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              클리어
            </Button>
          </div>
        ) : (
          <div className="flex gap-2 pt-2 border-t">
            {service.status === 'pending' && (
              <>
                <Button
                  onClick={() => onApprove?.(service.id)}
                  size="sm"
                  className="flex-1"
                >
                  승인
                </Button>
                <Button
                  onClick={() => onReject?.(service.id, service.service_name)}
                  size="sm"
                  variant="outline"
                  className="flex-1"
                >
                  거부
                </Button>
              </>
            )}
            {service.status === 'approved' && (
              <>
                <Button
                  onClick={() => onManagePlans?.(service)}
                  size="sm"
                  variant="outline"
                  className="flex-1"
                >
                  💳 플랜 관리
                </Button>
                <Button
                  onClick={() => onManageClient?.(service)}
                  size="sm"
                  variant="outline"
                  className="flex-1"
                >
                  🔑 로그인 연동
                </Button>
                <Button
                  onClick={() => onSuspend?.(service.id, service.service_name)}
                  size="sm"
                  variant="outline"
                  className="flex-1"
                >
                  일시 중지
                </Button>
                <Button
                  onClick={() => onDelete?.(service.id, service.service_name)}
                  size="sm"
                  variant="destructive"
                  className="flex-1"
                >
                  삭제
                </Button>
              </>
            )}
            {service.status === 'suspended' && (
              <>
                <Button
                  onClick={() => onResume?.(service.id, service.service_name)}
                  size="sm"
                  className="flex-1"
                >
                  재시작
                </Button>
                <Button
                  onClick={() => onDelete?.(service.id, service.service_name)}
                  size="sm"
                  variant="destructive"
                  className="flex-1"
                >
                  삭제
                </Button>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ServiceCard;

